export interface PushSubscriptionOwnershipStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type PushSubscriptionOwnership = 'owned' | 'missing' | 'foreign';

export interface PushSubscriptionOwnershipManager {
  classify(userId: string, endpoint: string): Promise<PushSubscriptionOwnership>;
  claim(userId: string, endpoint: string): Promise<boolean>;
  clear(): boolean;
}

interface StoredPushSubscriptionOwnership {
  version: 1;
  userId: string;
  endpointFingerprint: string;
}

export const PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY = 'mors:push-subscription-ownership';

const FINGERPRINT_PATTERN = /^[0-9a-f]{64}$/u;

const warnOwnershipFailure = (phase: 'read' | 'hash' | 'write' | 'clear'): void => {
  console.warn('[push.ownership] Не удалось обработать локального владельца подписки', {
    kind: 'local-state-failure',
    phase,
  });
};

const parseOwnership = (serialized: string): StoredPushSubscriptionOwnership | null => {
  try {
    const value = JSON.parse(serialized) as Record<string, unknown>;
    if (
      value.version !== 1 ||
      typeof value.userId !== 'string' ||
      value.userId.length === 0 ||
      value.userId.length > 128 ||
      typeof value.endpointFingerprint !== 'string' ||
      !FINGERPRINT_PATTERN.test(value.endpointFingerprint)
    ) {
      return null;
    }

    return {
      version: 1,
      userId: value.userId,
      endpointFingerprint: value.endpointFingerprint,
    };
  } catch {
    return null;
  }
};

const fingerprintEndpoint = async (endpoint: string): Promise<string | null> => {
  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));

    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    warnOwnershipFailure('hash');
    return null;
  }
};

export const createPushSubscriptionOwnershipManager = (
  storage: PushSubscriptionOwnershipStorage,
): PushSubscriptionOwnershipManager => {
  const read = (): StoredPushSubscriptionOwnership | 'invalid' | null => {
    try {
      const serialized = storage.getItem(PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY);
      if (!serialized) {
        return null;
      }

      const ownership = parseOwnership(serialized);
      if (!ownership) {
        warnOwnershipFailure('read');
        return 'invalid';
      }

      return ownership;
    } catch {
      warnOwnershipFailure('read');
      return 'invalid';
    }
  };

  return {
    classify: async (userId, endpoint) => {
      const ownership = read();
      if (!ownership) {
        return 'missing';
      }

      if (ownership === 'invalid') {
        return 'foreign';
      }

      const endpointFingerprint = await fingerprintEndpoint(endpoint);
      if (!endpointFingerprint) {
        return 'foreign';
      }

      return ownership.userId === userId && ownership.endpointFingerprint === endpointFingerprint ? 'owned' : 'foreign';
    },
    claim: async (userId, endpoint) => {
      const endpointFingerprint = await fingerprintEndpoint(endpoint);
      if (!endpointFingerprint) {
        return false;
      }

      try {
        storage.setItem(
          PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY,
          JSON.stringify({ version: 1, userId, endpointFingerprint } satisfies StoredPushSubscriptionOwnership),
        );
        return true;
      } catch {
        warnOwnershipFailure('write');
        return false;
      }
    },
    clear: () => {
      try {
        storage.removeItem(PUSH_SUBSCRIPTION_OWNERSHIP_STORAGE_KEY);
        return true;
      } catch {
        warnOwnershipFailure('clear');
        return false;
      }
    },
  };
};
